/**
 * Shared noise classifier for client-side issues, used by BOTH reporting paths:
 * the global `window` error/rejection listeners ({@link ./errorReporting}) and the
 * PostHog `$exception` → Telegram bridge ({@link ./analytics}). Keeping the single
 * source of truth here — a pure module with no `clientLogApi`/`apiConfig` import —
 * lets the analytics module reuse it without a load-time dependency that would throw
 * when `VITE_API_URL` is unset.
 */

import { ERROR_MESSAGES, GameErrorCode, type GameErrorCodeType } from '../../../shared/domain/errorCodes';

export type ClientIssueDisposition = 'error' | 'warn' | 'ignore';

/**
 * Game error codes that represent an EXPECTED user-facing state, not an app bug: the
 * player's pet is too tired/sick/hungry, an action is on cooldown, they're short on
 * Suika/XP, or they hit a rate/payment gate. The backend returns these as ordinary 4xx
 * responses and the UI already shows the message as a toast — so the resulting
 * `BackendRequestError` (or a console.error of one, which PostHog's console capture turns
 * into a `$exception`) must never page us or fill error.log. Excludes codes that DO signal
 * a real problem worth an alert (payment-integrity, save/concurrency, config-not-found).
 */
const EXPECTED_VALIDATION_CODES: readonly GameErrorCodeType[] = [
  GameErrorCode.PET_IS_EGG,
  GameErrorCode.PET_IS_HIBERNATING,
  GameErrorCode.PET_TOO_SICK_TO_TRAIN,
  GameErrorCode.PET_TOO_SICK_TO_REST,
  GameErrorCode.PET_TOO_TIRED_TO_TRAIN,
  GameErrorCode.PET_TOO_HUNGRY_TO_REST,
  GameErrorCode.PET_TOO_ENERGETIC_TO_REST,
  GameErrorCode.PET_ALREADY_RESTING,
  GameErrorCode.PET_NOT_RESTING,
  GameErrorCode.ENERGY_REFILL_ON_COOLDOWN,
  GameErrorCode.NOT_ENOUGH_XP_FOR_EVOLUTION,
  GameErrorCode.NOT_ENOUGH_XP_FOR_LEVEL_UP,
  GameErrorCode.ALREADY_MAX_STAGE,
  GameErrorCode.RATE_LIMIT_EXCEEDED,
  GameErrorCode.ADOPTION_LIMIT_EXCEEDED,
  GameErrorCode.MINIMUM_PAYMENT_REQUIRED,
  GameErrorCode.RARE_PET_PAYMENT_REQUIRED,
  GameErrorCode.EPIC_PET_PAYMENT_REQUIRED,
  GameErrorCode.LEGENDARY_PET_PAYMENT_REQUIRED,
  GameErrorCode.INSUFFICIENT_SUIKA,
];

/**
 * Stable, value-free stems of the expected-validation messages. Resolving each message with
 * no `data` yields its base text (dynamic tails like "Need 50 more XP" are appended only when
 * data is present), so a substring match on these stems catches the message whether or not it
 * carries interpolated values — and whether it arrives bare or prefixed ("BackendRequestError:
 * …"). Built once at module load from the single source of truth (`ERROR_MESSAGES`).
 */
const EXPECTED_VALIDATION_STEMS: readonly string[] = EXPECTED_VALIDATION_CODES.map((code) => {
  const messageOrFn = ERROR_MESSAGES[code];
  // Dynamic tails split on the first sentence boundary that precedes interpolation; the leading
  // clause is stable, so keep text up to the first "." / "$" and trim.
  const base = typeof messageOrFn === 'function' ? messageOrFn() : messageOrFn;
  // Keep only the leading clause, up to the first sentence boundary — punctuation ("." / "!" /
  // "?") followed by whitespace or end-of-string, so a decimal like "0.09 SUI" isn't split
  // mid-number. The dynamic tail ("Need 50 more XP", "Try again in 4m") lives after it, so the
  // stem stays stable whether or not data was interpolated.
  return base.split(/[.!?](?:\s|$)/)[0].trim();
}).filter((stem) => stem.length > 0);

/**
 * Classify browser noise separately from actual app crashes. Browser extensions, blocked
 * telemetry, private-mode storage, and a stale lazy chunk are useful diagnostics but should
 * never page us or fill error.log. The precise render/API failures we own stay at error level.
 */
export function classifyClientIssue(message: string, context = ''): ClientIssueDisposition {
  const details = `${message}\n${context}`;

  // Expected gameplay validation surfaced as an error (a 4xx BackendRequestError, or a
  // console.error of one). The user already sees the message as a toast — it's a business
  // rule, not a bug — so drop it from every channel. A generic "insufficient … balance" /
  // "not enough … balance" also covers mode-specific PVP gates (e.g. "Insufficient Suika
  // balance for ranked battle") that aren't in the shared ERROR_MESSAGES catalog.
  // A name collision on adopt / rename / egg-hatch is a 4xx the form already shows inline
  // ("Name \"X\" is already taken") — a business rule, not a bug. Not in ERROR_MESSAGES
  // (each surface builds its own copy), so match the stable clause.
  if (
    EXPECTED_VALIDATION_STEMS.some((stem) => message.includes(stem)) ||
    /\b(?:insufficient|not enough)\b[^.]*\bbalance\b/i.test(message) ||
    /\bis already taken\b/i.test(message)
  ) {
    return 'ignore';
  }

  // Chrome extension message-passing faults: an extension's content script talks to a service
  // worker that has been torn down. It fires on OUR page (so it reaches this net) but originates
  // entirely in the extension — including the follow-on "reading 'type'" TypeError, which is why
  // the CONTEXT is matched too, not just the message.
  if (/\bcould not establish connection\b|\breceiving end does not exist\b/i.test(details)) {
    return 'ignore';
  }

  // An injected wallet provider that can't be reached (extension locked, disabled, or removed
  // mid-session). Unactionable for us and never a crash — the login UI already reports it.
  if (/\bfailed to connect to (?:metamask|phantom|coinbase|rabby|brave)\b/i.test(details)) {
    return 'ignore';
  }

  // Third-party wallet/browser-extension script, not code served by this app.
  if (/\bTRPCClientError\b|user-script:/i.test(details)) return 'ignore';

  // Injected wallet extensions (e.g. Rabby) log their OWN internal faults through the page's
  // console — "'eth_accounts' unexpectedly updated accounts. Please report this bug." asks the
  // user to report to the WALLET vendor, not us. A mention of RainbowKit is likewise external:
  // this app uses Reown AppKit, never RainbowKit, so anything probing for it is an extension,
  // not our code. Neither is a Suikamon bug — drop them.
  if (/\bunexpectedly updated accounts\b|\brainbowkit\b/i.test(details)) return 'ignore';

  // Diagnostics emitted by injected wallet middleware and Reown's own remote services. They
  // describe extension transport/configuration state, not application failures, and the wallet
  // UI already owns retry/recovery.
  if (
    /\bobjectmultiplex\s*-\s*orphaned data\b|\bstreammiddleware\s*-\s*unknown response id\b|\binvalid isunlocked parameter\b|\berror checking cross-origin-opener-policy\b|\bfailed to fetch usage\b|\breown config\].*\bfailed to fetch remote project configuration\b/i.test(details)
  ) {
    return 'ignore';
  }

  // Wallet signing failures are handled inline by LoginButton. AppKit also logs the same
  // rejection through its SDK console, and MetaMask logs a second diagnostic if another request
  // was already pending; neither duplicate is an application exception.
  if (
    /\bpersonal_sign\b.*\balready pending\b|\bswixutil:requestsignmessage\b|\bwagmiadapter:signmessage\s*-\s*sign message failed\b/i.test(details)
  ) {
    return 'ignore';
  }

  // EIP-1193 "user rejected the request" (code 4001) — a deliberate user action when they
  // dismiss a wallet connect/sign prompt (e.g. RabbyMobile's "Not Allowed"). These surface as
  // floating rejections from the wallet's own injected provider on an auto-reconnect, so there's
  // no try/catch of ours to attach; drop them rather than page on expected user behavior.
  if (/"code":\s*4001\b|\bcode:?\s*4001\b|\buser (?:rejected|denied|cancell?ed|declined)\b/i.test(details)) {
    return 'ignore';
  }

  if (
    /\b(?:load failed|failed to fetch|importing a module script failed|indexeddb:set:internalerror|invalid wallet address format)\b/i.test(message)
  ) {
    return 'warn';
  }

  // Transient third-party conditions that degrade gracefully and need no page:
  //  • Vite's `vite:preloadError` for a stylesheet — a network/adblock hiccup or a stale
  //    `index.html` pointing at a replaced hash; the route still renders (JS chunk staleness is
  //    handled by `lazyWithReload`).
  //  • A social-login timeout raised inside the wallet SDK (not our string) — the user retries.
  //  • PostHog's own ingestion rejecting a request ("Forbidden: Nonce mismatch") — telemetry,
  //    not gameplay.
  if (
    /\bunable to preload css\b|\bsocial login timed out\b|\bnonce mismatch\b/i.test(message)
  ) {
    return 'warn';
  }

  return 'error';
}
